import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Box,Grid,Card,CardContent,Typography,Divider,IconButton,Dialog,DialogTitle,DialogContent,Button,
  Table,TableBody,TableCell,TableRow,TableHead,Chip,Tooltip,CircularProgress,} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form } from "formik";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import ReusableButton from "@/components/buttons/StyledButton";
import { CardWithActions, CustomAlert } from "@/components";
import CurveChart from "@/pages/pms/common/CurveChart";
import BarChart from "@/pages/pms/common/BarChart";
import { toast } from "@/components/toastify";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FormikTextInput from "@/components/forms/FormikTextInput";
import FormikTextarea from "@/components/forms/FormikTextarea";
import SubmitButtonWithLoader from "@/components/forms/SubmitButtonWithLoader";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import StickyNote2RoundedIcon from '@mui/icons-material/StickyNote2Rounded';
import {DownloadIcon,} from "@/icons";
import {
  useGetAsHRDropdownQuery,
  useGetEmpHistoryQuery,
  useUpdateAsHRMutation,useLazyBhexportQuery,useGetBhchartQuery,
} from "@/api/services/pms/AsHR/asHRApi";
import { useFetchSettingsRowsQuery } from "@/api/services/Settings/business/Business";
import { useGetDhBudgetQuery } from "@/api/services/pms/Employee/employeeApi";
import { sortPmsYears } from "../common/PmsyearSort";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";


const RATING_OPTIONS_DATA = [
  { ratingName: "EE", percentage: 12 },
  { ratingName: "SC", percentage: 10 },
  { ratingName: "ME", percentage: 8 },
  { ratingName: "PC", percentage: 0 },
];

const RATING_SELECT_OPTIONS = RATING_OPTIONS_DATA.map((r) => ({ label: r.ratingName, value: r.ratingName }));

const PROMOTION_TYPE_OPTIONS = [
  { value: "No Movement", label: "No Movement" },
  { value: "PIP", label: "PIP" },
  { value: "Promotion", label: "Promotion" },
  { value: "MarketStandard or Correction", label: "Market Standard or Correction" },
  { value: "Promotion & MarketStandard or Correction", label: "Promotion & Market Standard or Correction" },
 ];

const round2 = (n) => Math.round(n);

const getBaseIncrementPct = (values) => {
  const proRated = parseFloat(values.proRatedIncreasePercentage) || 0;
  const promoPct = parseFloat(values.promotionIncrementPercentage) || 0;
  const correction = parseFloat(values.promotionCorrections) || 0;
  const pt = values.promotionType || "No Movement";
  if (pt === "Promotion") return proRated + promoPct;
    if (pt === "PIP" || pt === "PC") return 0;
  if (pt === "MarketStandard or Correction") return proRated + correction;
  if (pt === "Promotion & MarketStandard or Correction") return proRated + promoPct + correction;
  return proRated;
};

const HrHeadPMS = () => {
  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [empDetails, setEmpDetails] = useState({});
  const [submittedEmps, setSubmittedEmps] = useState({});
  const [historyOpen, setHistoryOpen] = useState(false);
  const [filters, setFilters] = useState({
    pmsYear: "",
    divHeadId: "",
    empId: "",
  });
    const localStorageData = JSON.parse(localStorage.getItem("loginDetails"));
  const userToken = jwtDecode(localStorageData?.token);
  const activeRole = userToken?.userdetails?.activeRole;
  const isHRHead = activeRole === "HR Head";

  const { data: designation = [] } = useFetchSettingsRowsQuery("Designation");
  const [updateHR, { isLoading: updateHRLoading }] = useUpdateAsHRMutation();
  const [triggerExport, { isFetching: exportLoading }] = useLazyBhexportQuery();


 const handleExport = async () => {
   try {
     const res = await triggerExport({
       divisionheadid: filters.divHeadId || "",
       reportingmanagerid: filters.repManId || "",
       grade: filters.grade || "",
     }).unwrap();
 
     const url = window.URL.createObjectURL(res);
 
     const a = document.createElement("a");
 
     const now = new Date();
 
     const timestamp = now
       .toISOString()
       .replace(/[-:]/g, "")
       .replace("T", "_")
       .split(".")[0];
 
     const fileName = `HR_PMS_${timestamp}.xlsx`;
 
     a.href = url;
     a.download = fileName;
 
     document.body.appendChild(a);
     a.click();
 
     document.body.removeChild(a);
     window.URL.revokeObjectURL(url);
 
     toast("success", "File downloaded successfully");
   } catch (error) {
     console.error(error);
     toast("error", "Export failed");
   }
 };
  const designationOptions = useMemo(
    () => designation?.data?.map((des) => ({ value: des.name, label: des.name })) || [],
    [designation]
  );

  //  Dropdown API 
 const dropdownQueryParams = {
    level: "HR Head",
    pmsYear: filters.pmsYear,
  };

  if (filters.divHeadId) dropdownQueryParams.divHeadId = filters.divHeadId;
  if (filters.reviewerId) dropdownQueryParams.reviewerId = filters.reviewerId;
  if (filters.repManId) dropdownQueryParams.repManId = filters.repManId;

  const { data: dropdownData, isLoading: dropdownLoading } =
    useGetAsHRDropdownQuery(dropdownQueryParams);

       const pmsYearOptions = sortPmsYears(dropdownData?.pmsYears);
    
  const employeeOptions =
    dropdownData?.employee?.map((member) => ({
      label: `${member.empName} - ${member.empId || ""}`,
      value: member.empId || "",
    })) || [];

  const reportingManagerOptions =
    dropdownData?.repManagers?.map((manager) => ({
      label: `${manager.empName} - ${manager.empId || ""}`,
      value: manager.empId || "",
    })) || [];

  // const reviewerOptions =
  //   dropdownData?.reviewers?.map((reviewer) => ({
  //     label: `${reviewer.empName} - ${reviewer.empId || ""}`,
  //     value: reviewer.empId || "",
  //   })) || [];

  const divisionHeadOptions =
    dropdownData?.divisionHeads?.map((head) => ({
      label: `${head.empName} - ${head.empId || ""}`,
      value: head.empId || "",
    })) || [];


  const gradeOptions =
    dropdownData?.grade?.map((grade) => ({
      label: grade,
      value: grade,
    })) || [];


  useEffect(() => {
    if (pmsYearOptions.length > 0 && !filters.pmsYear) {
      const latestYear = pmsYearOptions[0].value;
      setFilters((prev) => ({ ...prev, pmsYear: latestYear }));
    }
  }, [pmsYearOptions]);
    

  //  DH Budget API (pmsYear required, optionally dhId/empId) 
  const dhBudgetParams = useMemo(() => {
    const params = { pmsYear: filters.pmsYear };
    if (filters.divHeadId) params.dhId = filters.divHeadId;
    if (selectedEmpId) params.empId = selectedEmpId;
    return params;
  }, [filters.pmsYear, filters.divHeadId, selectedEmpId]);

  const { data: dhBudgetData } = useGetDhBudgetQuery(dhBudgetParams, {
    skip: !filters.pmsYear,
  });

    const budgetData = useMemo(() => {
       const data = dhBudgetData?.data;
       return {
         totalBudget: parseFloat(data?.target) || 0,
         backendUsed: parseFloat(data?.totalConsumed) || 0,
         remainingAmount: parseFloat(data?.remainingAmount) || 0,
       };
     }, [dhBudgetData]);
   
  
    const TOTAL_BUDGET = budgetData.totalBudget;

  //  Chart API (division-level totals, no empId filter) 
  const { data: analyticsData } = useGetBhchartQuery(
    { divisionheadid: filters.divHeadId || undefined, pmsYear: filters.pmsYear },
    { skip: !filters.pmsYear }
  );

  //  Employee History API (single source for selected emp details + history) 
  const { data: historyData } = useGetEmpHistoryQuery(selectedEmpId, {
    skip: !selectedEmpId,
  });

  // Auto-populate Division Head and Reporting Manager when employee is directly selected
    useEffect(() => {
      const records = historyData?.data?.data || historyData?.data || [];
      const list = Array.isArray(records) ? records : [];
      const empDetails = list[0]?.employeeDetails;
      if (empDetails && selectedEmpId) {
        const dhId = empDetails.divisionHeadId;
        const rmId = empDetails.repManagerId;
        if (dhId || rmId) {
          setFilters((prev) => ({
            ...prev,
            ...(dhId && { divHeadId: dhId }),
            ...(rmId && { repManId: rmId }),
          }));
        }
      }
    }, [historyData, selectedEmpId]);
  
  const decodeBase64 = (val) => {
    try {
      const decoded = atob(val);
      const num = parseFloat(decoded);
      return isNaN(num) ? null : num;
    } catch {
      return null;
    }
  };

  const historyRows = useMemo(() => {
    const records = historyData?.data?.data || historyData?.data || [];
    const list = Array.isArray(records) ? records : [];
    return list.map((rec, idx) => {
      const currentCTC = decodeBase64(rec.currentCTC);
      const prevCTC = idx < list.length - 1 ? decodeBase64(list[idx + 1].currentCTC) : null;
      const ctcChange = currentCTC != null && prevCTC != null ? currentCTC - prevCTC : null;
      const revisedCTC = decodeBase64(rec.revisedCTC);
      const incrementAmounts = decodeBase64(rec.incrementAmount);
      const prevDesignation = idx < list.length - 1 ? list[idx + 1].designation : null;
      return { ...rec, decodedCTC: currentCTC, ctcChange, prevDesignation, revisedCTC, incrementAmounts };
    });
  }, [historyData]);

  // Latest record (0th index) = selected employee details
  const selectedEmp = useMemo(() => selectedEmpId ? (historyRows[0] || null) : null, [historyRows, selectedEmpId]);

  // Live budget: only track the DIFFERENCE between local edit and initial form value
  const initialIncRef = useRef(null);

  const liveLocalIncrement = useMemo(() => {
    if (!selectedEmpId || !empDetails[selectedEmpId]?.incrementAmount) return 0;
    const localInc = parseFloat(empDetails[selectedEmpId].incrementAmount) || 0;
    // Use the initial form increment as baseline (what API already accounts for)
    if (initialIncRef.current === null) initialIncRef.current = localInc;
    return localInc - initialIncRef.current;
  }, [selectedEmpId, empDetails]);

  // Reset ref when employee changes
  useEffect(() => {
    initialIncRef.current = null;
  }, [selectedEmpId]);

const usedBudget = budgetData.backendUsed + liveLocalIncrement;
  const remainingBudget = budgetData.remainingAmount - liveLocalIncrement;
  const budgetPct = TOTAL_BUDGET > 0 ? Math.min(100, (usedBudget / TOTAL_BUDGET) * 100) : 0;
  
  const hasCurrentYearData = useMemo(() => {
    return historyRows.some((r) => r.pmsYear === filters.pmsYear);
  }, [historyRows, filters.pmsYear]);

  const currentYearRecord = useMemo(() => {
    const rec = historyRows.find((r) => r.pmsYear === filters.pmsYear) || historyRows[0] || null;
    if (!rec) return null;
    const recIdx = historyRows.indexOf(rec);
    const prevRec = recIdx < historyRows.length - 1 ? historyRows[recIdx + 1] : null;
    const prevCTC = prevRec ? decodeBase64(prevRec.currentCTC) : null;
    return { ...rec, prevCTC, prevDesignation: prevRec?.designation || null };
  }, [historyRows]);

  //  Chart data with local rating overrides 
  const apiChartCounts = useMemo(() => {
    const data = analyticsData?.data;
    if (!data) return {};
    if (Array.isArray(data)) {
      const counts = {};
      data.forEach((item) => {
        counts[item.ratingName] = item.empCount;
      });
      return counts;
    }
    const counts = {};
    Object.entries(data).forEach(([key, value]) => {
      counts[key] = Number(value);
    });
    return counts;
  }, [analyticsData]);

  const mergedChartCounts = useMemo(() => {
    const counts = { ...apiChartCounts };

    if (selectedEmpId && empDetails[selectedEmpId]?.bhFinalRating) {
      const localRating = empDetails[selectedEmpId].bhFinalRating;
      const originalRating = selectedEmp?.bhFinalRating || selectedEmp?.divisionHeadRating;
      const status = selectedEmp?.bhStatus || selectedEmp?.status;
      const isSaved = status === "Save" || status === "Saved" || status === "Submitted" || !!originalRating;

      if (isSaved && originalRating) {
        // Already counted in API chart — reduce old, increase new
        if (localRating !== originalRating) {
          if (counts[originalRating] !== undefined) {
            counts[originalRating] = Math.max(0, counts[originalRating] - 1);
          }
          counts[localRating] = (counts[localRating] || 0) + 1;
        }
      } else {
        // Pending: chart doesn't include this employee — just add
        counts[localRating] = (counts[localRating] || 0) + 1;
      }
    }

    return counts;
  }, [apiChartCounts, selectedEmpId, empDetails, selectedEmp]);

  const chartData = useMemo(() => {
    return Object.entries(mergedChartCounts).map(([key, value]) => ({
      empId: key,
      ratingNumber: value,
    }));
  }, [mergedChartCounts]);

  const barData = useMemo(() => {
    const total = Object.values(mergedChartCounts).reduce((a, b) => a + b, 0);
    if (total === 0) return [];
    return Object.entries(mergedChartCounts).map(([key, value]) => ({
      label: key,
      percentage: Math.round((value / total) * 100),
      count: value,
    }));
  }, [mergedChartCounts]);

  const handleEmpSelect = (empId) => {
    setSelectedEmpId(empId);
    setFilters((prev) => ({ ...prev, empId }));
  };

  const handleDetailChange = (empId, field, value) => {
    setEmpDetails((prev) => ({
      ...prev,
      [empId]: { ...prev[empId], [field]: value },
    }));
  };

  //  HEPL Calculation helpers 
  const getIncrementMonths = (record) => {
    try {
      return parseFloat(atob(record?.incrementMonthsOld)) || 0;
    } catch { return 0; }
  };

  const getProRated = (percentage, incrementMonths) =>
    incrementMonths > 0 ? Math.round((percentage / 12) * incrementMonths) : percentage;

  const computeHEPLFinalValues = (values) => {
    const ctc = parseFloat(values.currentCTC) || 0;
    const basePct = getBaseIncrementPct(values);
    const additionalPct = parseFloat(values.additionalPercentage) || 0;
    const additionalAmt = parseFloat(values.additionalAmount) || 0;
    const totalPct = round2(basePct + additionalPct);
    const baseIncrementAmount = round2((ctc * basePct) / 100);
    const incrementAmount = round2(baseIncrementAmount + additionalAmt);
    return {
      finalIncrement: String(totalPct),
      incrementAmount: String(incrementAmount),
      revisedCTC: String(round2(ctc + incrementAmount)),
    };
  };

  // Build initial form values for an employee
  const getInitialFormValues = (record) => {
    const isNAPS = record?.payrollStatus === "NAPS" || record?.payrollStatus === "NATS";
    const smartDecode = (val) => {
      if (!val && val !== 0) return "0";
      if (!isNaN(parseFloat(val)) && isFinite(val)) return String(val);
      try {
        let d = atob(val);
        if (isNaN(parseFloat(d))) { try { d = atob(d); } catch {} }
        return d;
      } catch { return val || "0"; }
    };
     const ctc = record?.currentCTC ? smartDecode(record.currentCTC) : "0";
    const isQcRejected = record?.qcStatus === "Rejected" && record?.hrStatus === "Saved";
    let initRating = isQcRejected
      ? (record?.hrHeadRating || record?.divisionHeadRating || record?.bhFinalRating || "")
      : (record?.divisionHeadRating || record?.bhFinalRating || "");
    let initPromoType = isQcRejected
      ? (record?.promotionType || record?.divisionHeadRecommendation || "")
      : (record?.divisionHeadRecommendation || record?.promotionType || "");
    if (initRating === "PC" && initPromoType !== "PIP") initPromoType = "PIP";
    if (initPromoType === "PIP" && initRating !== "PC") initRating = "PC";

    if (isNAPS) {
      const ratingObj = RATING_OPTIONS_DATA.find((r) => r.ratingName === initRating);
      const hikePM = record?.hikePerMonth ? smartDecode(record.hikePerMonth) : (ratingObj?.percentage ?? "");
      const hikeNum = parseFloat(hikePM) || 0;
      const incAmt = hikeNum * 12;
      const ctcNum = parseFloat(ctc) || 0;
      return {
        bhFinalRating: initRating,
        promotionType: initPromoType,
        department: record?.promotionDesignation || "",
        hikePerMonth: hikePM,
        incrementAmount: record?.incrementAmount ? smartDecode(record.incrementAmount) : String(Math.round(incAmt)),
        currentCTC: ctc,
        additionalAmount: record?.additionalIncrementAmount ? smartDecode(record.additionalIncrementAmount) : "0",
        additionalPercentage: record?.additionalIncrementPercentage ? smartDecode(record.additionalIncrementPercentage) : "0",
        revisedCTC: record?.revisedCTC ? smartDecode(record.revisedCTC) : String(Math.round(ctcNum + incAmt)),
        hrRemark: record?.hrRemarks || "",
      };
    }

    const incrementMonths = getIncrementMonths(record);
    const ratingObj = RATING_OPTIONS_DATA.find((r) => r.ratingName === initRating);
    const pct = ratingObj?.percentage || 0;
    const proRated = ratingObj ? getProRated(pct, incrementMonths) : 0;
    const ctcNum = parseFloat(ctc) || 0;

    const initVals = {
      bhFinalRating: initRating,
      promotionType: initPromoType,
      department: record?.promotionDesignation || "",
     promotionGrade: record?.promotionGrade || "",
      incrementAsPerRating: (initRating === "PC" || initPromoType === "PIP") ? 0
        : record?.percentIncreaseAsPerRating
          ? (() => { try { const d = atob(record.percentIncreaseAsPerRating); return parseFloat(d) || pct || ""; } catch { return pct || ""; } })()
          : (pct || ""),
      promotionIncrementPercentage: record?.promotionIncrementPercentage
        ? (() => { const d = parseFloat(smartDecode(record.promotionIncrementPercentage)); return !isNaN(d) ? d : 0; })()
        : 0,
      promotionCorrections: record?.corrections || record?.promotionCorrections
        ? (() => { const d = parseFloat(smartDecode(record.corrections || record.promotionCorrections)); return !isNaN(d) ? d : 0; })()
        : 0,
      proRatedIncreasePercentage: record?.proratedIncreasePercentBasisOfDoj
        ? (() => { try { const d = atob(record.proratedIncreasePercentBasisOfDoj); return parseFloat(d) || proRated; } catch { return proRated; } })()
        : proRated,
      currentCTC: ctc,
      additionalAmount: record?.additionalIncrementAmount
        ? (() => { const d = smartDecode(record.additionalIncrementAmount); return parseFloat(d) || 0; })()
        : 0,
      additionalPercentage: record?.additionalIncrementPercentage
        ? (() => { const d = smartDecode(record.additionalIncrementPercentage); return parseFloat(d) || 0; })()
        : 0,
      hrRemark: record?.hrRemarks || "",
    };

    const computed = computeHEPLFinalValues(initVals);
    initVals.finalIncrement = record?.finalIncrementPercent
      ? (() => { const d = parseFloat(smartDecode(record.finalIncrementPercent)); return !isNaN(d) ? d : parseFloat(computed.finalIncrement); })()
      : parseFloat(computed.finalIncrement);
    initVals.incrementAmount = record?.incrementAmount
      ? (() => { const d = parseFloat(smartDecode(record.incrementAmount)); return !isNaN(d) ? d : parseFloat(computed.incrementAmount); })()
      : parseFloat(computed.incrementAmount);
    initVals.revisedCTC = record?.revisedCTC
      ? (() => { const d = parseFloat(smartDecode(record.revisedCTC)); return !isNaN(d) ? d : parseFloat(computed.revisedCTC); })()
      : parseFloat(computed.revisedCTC);

    return initVals;
  };

  const handleFormSubmit = async (values) => {
    if (!selectedEmp) return;
    const isNAPS = selectedEmp.payrollStatus === "NAPS" || selectedEmp.payrollStatus === "NATS";
    const { log, ...formValues } = values;

    const formData = {
      empId: selectedEmp.empId,
      pmsYear: selectedEmp.pmsYear,
      levelName: "HR Head",
      action: "Submit",
      finalRating: "HR Head",
      finalRatingValue: formValues.bhFinalRating,
      promotionRecommendation: selectedEmp.promotion || "",
      promotionType: formValues.promotionType,
      promotionDesignation: formValues.department,
      incrementMonthsOld: selectedEmp.incrementMonthsOld,
      hrRemark: formValues.hrRemark,
      payRoleStatus: selectedEmp.payrollStatus,
      incrementAmount: formValues.incrementAmount,
      revisedCTC: formValues.revisedCTC,
      additionalIncrementAmount: formValues.additionalAmount,
      additionalIncrementPercentage: formValues.additionalPercentage,
      log: log || [],
    };

    if (isNAPS) {
      formData.hikePerMonth = formValues.hikePerMonth;
    } else {
      formData.proRatedIncreasePercentage = formValues.proRatedIncreasePercentage;
      formData.incrementAsPerRating = formValues.incrementAsPerRating;
      formData.finalIncrement = formValues.finalIncrement;
      formData.promotionCorrections = formValues.promotionCorrections;
      formData.promotionIncrementPercentage = formValues.promotionIncrementPercentage;
      formData.band = formValues.promotionGrade;
    }

    try {
      await updateHR(formData).unwrap();
      setEmpDetails((prev) => ({ ...prev, [selectedEmp.empId]: {
        ...values,
        additionalIncrementPercentage: values.additionalPercentage,
        additionalIncrementAmount: values.additionalAmount,
        hrStatus: "Submitted",
      } }));
      setSubmittedEmps((prev) => ({ ...prev, [selectedEmp.empId]: true }));
      toast("success", `Submitted successfully for ${selectedEmp.empName}`);
      console.log(formData,"formData")
    } catch (error) {
      toast("error", "Failed to submit");
    }
  };

  // Build read-only detail from API row for already-submitted records
  const getSubmittedDetailFromRow = (row) => {
    const isNAPS = row.payrollStatus === "NAPS" || row.payrollStatus === "NATS";
    const decode = (val) => { try { return atob(val); } catch { return val || "-"; } };
    return {
      bhFinalRating: row.hrHeadRating || row.bhFinalRating || "-",
      hrStatus: row.hrStatus || "",
      promotionType: row.promotionType || "-",
      department: row.promotionDesignation || "",
      promotionGrade: row.promotionGrade || "",
      incrementAsPerRating: row.percentIncreaseAsPerRating ? decode(row.percentIncreaseAsPerRating) : "0",
      proRatedIncreasePercentage: row.proratedIncreasePercentBasisOfDoj ? decode(row.proratedIncreasePercentBasisOfDoj) : "0",
      finalIncrement: row.finalIncrementPercent ? decode(row.finalIncrementPercent) : "0",
      hikePerMonth: row.hikePerMonth ? decode(row.hikePerMonth): "0",
      incrementAmount: row.incrementAmount ? decode(row.incrementAmount) : "0",
      currentCTC: row.currentCTC ? decode(row.currentCTC) : "-",
      revisedCTC: row.revisedCTC ? decode(row.revisedCTC) : "-",
      additionalIncrementPercentage: row.additionalIncrementPercentage ? decode(row.additionalIncrementPercentage) : "-",
      additionalIncrementAmount: row.additionalIncrementAmount ? decode(row.additionalIncrementAmount) : "-",
      promotionIncrementPercentage: row.promotionIncrementPercentage ? decode(row.promotionIncrementPercentage) : "-",
      hrRemark: row.hrRemarks || "",
    };
  };

  const [formResetKey, setFormResetKey] = useState(0);

  const handleCancel = () => {
    if (!selectedEmp) return;
    setEmpDetails((prev) => {
      const copy = { ...prev };
      delete copy[selectedEmp.empId];
      return copy;
    });
    setFormResetKey((k) => k + 1);
  };

  const handleFilterChange = useCallback((field, value, setFieldValue) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "divHeadId") {
        next.empId = "";
        setFieldValue?.("Employee", "");
        setSelectedEmpId("");
      }
      return next;
    });
  }, []);

  const handleReset = useCallback((resetForm) => {
    resetForm();
    setFilters({ pmsYear: filt, divHeadId: "", empId: "" });
    setSelectedEmpId("");
    setSubmittedEmps({});
    setEmpDetails({});
    setFormResetKey((k) => k + 1);
  }, []);


  return (
    <Card headerTitle="HRHEAD / HR Head" sx={{height:"100%"}}>
      <Typography sx={{ fontWeight: 600 ,mt:2,ml:3,fontSize:20}}>
        HR Head
      </Typography>      
      <Box p={1}>
        {/*  Top Filters */}
            <Formik
          initialValues={{ PMSYear: filters.pmsYear, DivisionHead: "", Employee: "", RepManager: "", grade: "" }}
          enableReinitialize
          onSubmit={() => {}}
        >
   {({ setFieldValue, resetForm }) => {
          // Sync Formik fields when filters change from employee auto-populate
          useEffect(() => {
            if (filters.divHeadId) setFieldValue("DivisionHead", filters.divHeadId);
            if (filters.repManId) setFieldValue("RepManager", filters.repManId);
          }, [filters.divHeadId, filters.repManId]);

          return (           <Form>
                                 <Grid container spacing={1} alignItems="center" sx={{ml:1,
                                   "& .MuiInputBase-root": { height: 32 },
                                  //  "& .MuiInputBase-input": { padding: "4px 8px !important", fontSize: "0.8rem" },
                                  //  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                                  //  "& .MuiSelect-select": { padding: "4px 8px !important", fontSize: "0.8rem" },
                                 }}>
                                   {/* <Grid item xs={12} sm={1.8}>
                                     <FormikSelectInput
                                       search={true}
                                       name="PMSYear"
                                       label="PMS Year"
                                       options={pmsYearOptions}
                                       value={filters.pmsYear}
                                       onChange={(e) => {
                                         const value = e.target.value;
                                         setFieldValue("pmsYear", value);
                                         handleFilterChange("pmsYear", value);
                                       }}
                                       placeholder="Select PMS Year"
                                     />
                                   </Grid> */}
                                   <Grid item xs={12} sm={2.5}>
                                     <FormikSelectInput
                                       search={true}
                                       name="DivisionHead"
                                       label="Division Head"
                                       options={divisionHeadOptions}
                                       value={filters.divHeadId}
                                       onChange={(e) => {
                                         const value = e.target.value;
                                         setFieldValue("DivisionHead", value);
                                         setFieldValue("Reviewer", "");
                                         setFieldValue("RepManager", "");
                                         setFieldValue("Employee", "");
                                         setFilters((prev) => ({
                                           ...prev,
                                           divHeadId: value,
                                           reviewerId: "",
                                           repManId: "",
                                           empId: "",
                                         }));
                                         handleFilterChange("divHeadId", value, setFieldValue);
                                       }}
                                       placeholder="Select Division Head"
                                     />
                                   </Grid>
                                   <Grid item xs={12} sm={2.5}>
                                     <FormikSelectInput
                                       search={true}
                                       name="RepManager"
                                       label="Reporting Manager"
                                       options={reportingManagerOptions}
                                       value={filters.repManId}
                                       onChange={(e) => {
                                         const value = e.target.value;
                                         setFieldValue("RepManager", value);
                                         setFieldValue("Employee", "");
                                         handleFilterChange("repManId", value, setFieldValue);
                                       }}
                                       placeholder="Select Reporting Manager"
                                     />
                                   </Grid>
                                   <Grid item xs={12} sm={2.5}>
                                     <FormikSelectInput
                                       search={true}
                                       name="Employee"
                                       label="Employee"
                                       options={employeeOptions}
                                       value={filters.empId}
                                       onChange={(e) => {
                                         const value = e.target.value;
                                         setFieldValue("Employee", value);
                                         handleFilterChange("empId", value, setFieldValue);
                                         setSelectedEmpId(value);
                                       }}
                                       placeholder="Select Employee"
                                     />
                                   </Grid>
                                   <Grid item xs={12} sm={1.2}>
                                     <FormikSelectInput
                                       search={true}
                                       name="grade"
                                       label="Grade"
                                       options={gradeOptions}
                                       value={filters.grade}
                                       onChange={(e) => {
                                         const value = e.target.value;
                                         setFieldValue("grade", value);
                                         handleFilterChange("grade", value);
                                       }}
                                       placeholder="Select Grade"
                                     />
                                   </Grid>
                                   <Grid item xs={12} sm="auto" sx={{mt:3}}>
                                    <Tooltip title="Clear Filters">
                                       <IconButton
                                         size="small"
                                         onClick={() => {
                                           resetForm();
                                           setFilters({
                                             pmsYear: "",
                                             manPower: "",
                                             empId: "",
                                             rollOfIntake: "",
                                             repManId: "",
                                             reviewerId: "",
                                             divHeadId: "",
                                             department: "",
                                             grade: "",
                                             page: 1,
                                             per_page: 10,
                                           });
                                           setSelectedEmpId("");
                                           setEmpDetails({});
                                           setSubmittedEmps({});
                                           setFormResetKey((k) => k + 1);
                                         }}
                                        sx={{
                                                color: "error.main",
                                                bgcolor: "error.lighter",
                                                border: "1px solid",
                                                borderColor: "error.light",
                                                boxShadow: 1,
                                                transition: "all 0.2s ease",
                                                "&:hover": {
                                                  bgcolor: "error.main",
                                                  color: "#fff",
                                                  transform: "scale(1.08)",
                                                },
                                              }}                                       >
                                         <RestartAltIcon fontSize="small" />
                                       </IconButton>
                                     </Tooltip>

                                   </Grid>
                                   <Grid item xs={12} sm="auto" sx={{mt:3}}>
                                     <Tooltip title="Export">
                                      <IconButton
                                        size="small"
                                        onClick={handleExport}
                                        disabled={exportLoading}
                                        sx={{
                                          bgcolor: "grey.200",
                                          color: "primary.main",
                                          "&:hover": { bgcolor: "grey.300" },
                                        }}
                                      >
                                         {exportLoading ? <CircularProgress size={18} /> : <DownloadIcon />}
                                       </IconButton>
                                     </Tooltip>
                                   </Grid>
                                   {TOTAL_BUDGET > 0 && (
                                     <Grid item xs={12} sm="auto" sx={{mt:1}}>
                                       <Tooltip
                                         title={`Increment Used: ₹${usedBudget.toLocaleString("en-IN")} / ₹${TOTAL_BUDGET.toLocaleString("en-IN")} • Remaining: ₹${remainingBudget.toLocaleString("en-IN")}`}
                                         arrow
                                       >
                                         <Box sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "default" }}>
                                           <Box sx={{ position: "relative", width: 60, height: 60, flexShrink: 0 }}>
                                             <Box component="svg" viewBox="0 0 44 44" sx={{ width: 60, height: 60, transform: "rotate(-90deg)" }}>
                                               {/* Thin track */}
                                               <circle cx="22" cy="22" r="17" fill="none" stroke="#43a047" strokeWidth="4" opacity={0.3} />
                                               {/* Thick filled arc */}
                                               <circle cx="22" cy="22" r="17" fill="none"
                                                 stroke={budgetPct > 90 ? "#e65100" : budgetPct > 70 ? "#f9a825" : "#43a047"}
                                                 strokeWidth="6" strokeLinecap="round"
                                                 strokeDasharray={`${(budgetPct / 100) * 106.8} 106.8`}
                                                 style={{ transition: "stroke-dasharray 0.4s ease, stroke 0.3s ease" }}
                                               />
                                               {/* End dot */}
                                               {(() => {
                                                 const angle = (budgetPct / 100) * 2 * Math.PI;
                                                 const x = 22 + 17 * Math.cos(angle);
                                                 const y = 22 + 17 * Math.sin(angle);
                                                 return <circle cx={x} cy={y} r="3" fill="#fff" stroke={budgetPct > 90 ? "#e65100" : budgetPct > 70 ? "#f9a825" : "#43a047"} strokeWidth="1.6" />;
                                               })()}
                                             </Box>
                                             <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                               <Typography sx={{ fontSize: 18, fontWeight: 950, lineHeight: 1, color: budgetPct > 90 ? "#e65100" : budgetPct > 70 ? "#f57f17" : "#1b5e20" }}>
                                                 {budgetPct.toFixed(0)}%
                                               </Typography>
                                               {/* <Typography sx={{ fontSize: 10, color: "text.secondary", lineHeight: 1, mt: 0.2 }}>Budget</Typography> */}
                                             </Box>
                                           </Box>
                                           <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
                                             <Typography sx={{ fontSize: 26,
                          fontWeight: 850, color: budgetPct > 90 ? "#e65100" : budgetPct > 70 ? "#f57f17" : "#1b5e20", lineHeight: 1.2 }}>
                                               ₹{usedBudget.toLocaleString("en-IN")}
                                               <Typography component="span" sx={{fontSize: 16, fontWeight: 650,color: "text.secondary" }}>
                                                 {" / "}₹{TOTAL_BUDGET.toLocaleString("en-IN")}
                                               </Typography>
                                             </Typography>
                      <Typography sx={{ fontSize: 14, color: "text.secondary", lineHeight: 1 }}>
                                               Remaining: ₹{remainingBudget.toLocaleString("en-IN")}
                                             </Typography>
                                           </Box>
                                         </Box>
                                       </Tooltip>
                                     </Grid>
                                   )}
                                 </Grid>
                               </Form>
          );
        }}
        </Formik>

        <Divider sx={{ my: 0.5 }} />

        {/*  Two Section Layout: Left = Emp Details, Right = Chart  */}
        <Grid container spacing={0.5} alignItems="stretch">
          {/* Left: Employee Details */}
          <Grid item xs={12} md={7} sx={{ display: "flex", flexDirection: "column" }}>
          <Card
                  variant="outlined"
                  sx={{
                    flexGrow: 1,
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    border: "none",
                    background: "linear-gradient(to bottom right, #ffffff, #f8f9fa)",
                  }}
                >
          {selectedEmp ? (
            <CardContent
              sx={{
                p: "8px !important",
              }}
            >
        {/* Header */}
            <Box
             sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1.2,
                mb: 0.6,
                borderRadius: 2,
                height:52,
                background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
                 border: "1px solid #80cbc4",
                boxShadow: "0 2px 8px rgba(25, 118, 210, 0.1)",
              }}
            >
          <Box>
            <Typography fontWeight={750} fontSize={15} color="#004d40">
              {selectedEmp.empName}
            </Typography>

            <Typography variant="caption" color="#004d40" sx={{ fontWeight: 600 }}>
              {selectedEmp.empId} • {selectedEmp.department} •  G ({selectedEmp.grade})            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={() => setHistoryOpen(true)}
           sx={{
              color: "#004d40",
              bgcolor: "rgba(21,101,192,0.12)",
              "&:hover": { bgcolor: "rgba(21,101,192,0.2)" },
            }}
          >
            <HistoryIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Ratings Section */}
              {/* <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "text.secondary", fontSize: 10, ml:0.5 }}
              >
                PERFORMANCE RATINGS
              </Typography> */}
              <Divider sx={{ mt: 0.8, borderBottomWidth: 0 }} />
             <Grid container>
  <Grid container spacing={1}>

    <Grid item xs={6}>
      <DetailRow
        label="Employee Self Rating"
        value={selectedEmp.selfRating}
        chipBg="#e3f2fd"
        chipText="#1565c0"
        chipBorder="1px solid #90caf9"
      />
    </Grid>

    {/* <Grid item xs={2}>
      <DetailRow
        label="RM Rating"
        value={selectedEmp.reportingManagerRating}
        chipBg="#e8f5e9"
        chipText="#2e7d32"
        chipBorder="1px solid #81c784"
      />
    </Grid>

    <Grid item xs={4.5}>
      <DetailRow
        label="Reviewer Rating"
        value={selectedEmp.reviewerRating}
        chipBg="#e8f5e9"
        chipText="#2e7d32"
        chipBorder="1px solid #81c784"
        remark={selectedEmp.remarks}
        recommendation={selectedEmp.reviewerRecommendation}
      />
    </Grid> */}

    <Grid item xs={6}>
      <DetailRow
        label="Division Head Rating"
        value={selectedEmp.divisionHeadRating}
       chipBg="#f3e5f5"
        chipText="#6a1b9a"
        chipBorder="1px solid #ce93d8"
        remark={selectedEmp.dhRemarks}
        recommendation={selectedEmp.divisionHeadRecommendation}
      />
    </Grid>

  </Grid>
          </Grid>

        <Divider sx={{ mt: 0.5, borderBottomWidth: 0 }} />

        {/* Action Section */}
        {selectedEmp.qcStatus === "Rejected" && !empDetails[selectedEmp.empId]?._editing ? (
          <Box>
            <Box
  sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 1,
    mb: 0.5,
    flexWrap: "wrap",
  }}
>
  {/* Left Section */}
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      flexWrap: "wrap",
      flex: 1,
      minWidth: 0,
    }}
  >
    {selectedEmp.qcStatus === "Rejected" && (
      <Chip
        label={`QC Status: ${selectedEmp.qcStatus}`}
        size="small"
        sx={{
          bgcolor: "#fbe9e7",
          color: "#d32f2f",
          border: "1px solid #ef9a9a",
          fontWeight: 600,
          fontSize: "0.75rem",
          height: 26,
        }}
      />
    )}

    {selectedEmp.qcRemarks && (
     <Tooltip
    title={
      selectedEmp.qcRemarks.length > 50
        ? selectedEmp.qcRemarks
        : ""
    }
    arrow
  >
    <Chip
      label={`QC Remarks: ${selectedEmp.qcRemarks}`}
      size="small"
      sx={{
        bgcolor: "#fff3e0",
        color: "#e65100",
        border: "1px solid #ffcc80",
        fontWeight: 600,
        fontSize: "0.75rem",
        maxWidth: 350,
        "& .MuiChip-label": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
      }}
    />
  </Tooltip>
    )}
  </Box>

  {/* Right Section */}
  <IconButton
    size="small"
    onClick={() => {
      setEmpDetails((prev) => ({
        ...prev,
        [selectedEmp.empId]: {
          ...prev[selectedEmp.empId],
          _editing: true,
        },
      }));
      setFormResetKey((k) => k + 1);
    }}
    sx={{
      color: "#f57f17",
      bgcolor: "rgba(245,127,23,0.08)",
      flexShrink: 0,
      "&:hover": {
        bgcolor: "rgba(245,127,23,0.15)",
      },
    }}
  >
    <EditOutlinedIcon fontSize="small" />
  </IconButton>
</Box>
            <SubmittedView
              detail={getSubmittedDetailFromRow(selectedEmp)}
              isNAPS={
                selectedEmp.payrollStatus === "NAPS" ||
                selectedEmp.payrollStatus === "NATS"
              }
              status={selectedEmp.status}
            />
          </Box>
        ) : (selectedEmp.qcStatus === "Rejected" && empDetails[selectedEmp.empId]?._editing) ? (
          <InlineActionForm
            key={`${selectedEmp.empId}-${formResetKey}`}
            record={selectedEmp}
            designationOptions={designationOptions}
            gradeOptions={gradeOptions}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setEmpDetails((prev) => {
                const copy = { ...prev };
                delete copy[selectedEmp.empId];
                return copy;
              });
              setFormResetKey((k) => k + 1);
            }}
            loading={updateHRLoading}
            getInitialFormValues={getInitialFormValues}
            getIncrementMonths={getIncrementMonths}
            getProRated={getProRated}
            computeHEPLFinalValues={computeHEPLFinalValues}
            onRatingChange={(rating) =>
              handleDetailChange(selectedEmp.empId, "bhFinalRating", rating)
            }
            onValuesChange={(vals) => {
              setEmpDetails((prev) => ({
                ...prev,
                [selectedEmp.empId]: { ...prev[selectedEmp.empId], ...vals },
              }));
            }}
          />
        ) : (!isHRHead ||
          submittedEmps[selectedEmp.empId] ||
          selectedEmp.status === "Submitted" ||
          selectedEmp.bhStatus === "Submitted" ||
          (selectedEmpId && historyRows.length > 0 && !hasCurrentYearData)) ? (
          <SubmittedView
            detail={
              (selectedEmpId && historyRows.length > 0 && !hasCurrentYearData)
                ? {
                    bhFinalRating: "-",
                    promotionType: "-",
                    department: "",
                    promotionGrade: "",
                    incrementAsPerRating: "-",
                    proRatedIncreasePercentage: "-",
                    finalIncrement: "-",
                    hikePerMonth: "-",
                    incrementAmount: "-",
                    currentCTC: "-",
                    revisedCTC: "-",
                    additionalIncrementPercentage: "-",
                    additionalIncrementAmount: "-",
                    hrRemark: "",
                  }
                : submittedEmps[selectedEmp.empId]
                  ? empDetails[selectedEmp.empId] || {}
                  : getSubmittedDetailFromRow(selectedEmp)
            }
            isNAPS={
              selectedEmp.payrollStatus === "NAPS" ||
              selectedEmp.payrollStatus === "NATS" ||
              selectedEmp.payrollStatus === "IC" 
            }
            status={selectedEmp.status || selectedEmp.bhStatus}
          />
        ) : ((selectedEmp.status === "Saved" || selectedEmp.status === "Save") && !empDetails[selectedEmp.empId]?._editing) ? (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
              <IconButton size="small" onClick={() => {
                setEmpDetails((prev) => ({
                  ...prev,
                  [selectedEmp.empId]: { ...prev[selectedEmp.empId], _editing: true },
                }));
                setFormResetKey((k) => k + 1);
              }} sx={{ color: "#f57f17", bgcolor: "rgba(245,127,23,0.08)", "&:hover": { bgcolor: "rgba(245,127,23,0.15)" } }}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
            <SubmittedView
              detail={getSubmittedDetailFromRow(selectedEmp)}
              isNAPS={
                selectedEmp.payrollStatus === "NAPS" ||
                selectedEmp.payrollStatus === "NATS"
              }
              status={selectedEmp.status}
            />
          </Box>
        ) : (
          <InlineActionForm
            key={`${selectedEmp.empId}-${formResetKey}`}
            record={selectedEmp}
            designationOptions={designationOptions}
            gradeOptions={gradeOptions}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              if (selectedEmp.qcStatus === "Rejected" || selectedEmp.status === "Saved" || selectedEmp.status === "Save") {
                setEmpDetails((prev) => {
                  const copy = { ...prev };
                  delete copy[selectedEmp.empId];
                  return copy;
                });
                setFormResetKey((k) => k + 1);
              } else {
                handleCancel();
              }
            }}
            loading={updateHRLoading}
            getInitialFormValues={getInitialFormValues}
            getIncrementMonths={getIncrementMonths}
            getProRated={getProRated}
            computeHEPLFinalValues={computeHEPLFinalValues}
            onRatingChange={(rating) =>
              handleDetailChange(
                selectedEmp.empId,
                "bhFinalRating",
                rating
              )
            }
            onValuesChange={(vals) => {
              setEmpDetails((prev) => ({
                ...prev,
                [selectedEmp.empId]: { ...prev[selectedEmp.empId], ...vals },
              }));
            }}
          />
        )}
      </CardContent>
    ) : (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        minHeight={350}
      >
        <Typography color="text.secondary">
          Please select an employee
        </Typography>
      </Box>
    )}
  </Card>
</Grid>

         {/* Right: Overall Chart */}
<Grid item xs={12} md={5} sx={{ display: "flex", flexDirection: "column" }}>
  <Card
    variant="outlined"
    sx={{
      flexGrow: 1,
      borderRadius: 2,
      boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
      border: "none",
      background: "linear-gradient(to bottom right, #ffffff, #fdfbfb)",
    }}
  >
    <CardContent sx={{ p: "8px !important" }}>

      {/* Header */}
      <Box
         sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
          p: 1,
          borderRadius: 2,
          background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
          border: "1px solid #80cbc4",
          boxShadow: "0 2px 8px rgba(0, 150, 136, 0.1)",
        }}
      >
        <AssessmentIcon sx={{ fontSize: 20, color: "#00695c" }} />
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 800,
            color: "#004d40",
          }}
        >
          Impact on Division
        </Typography>
      </Box>

      {/* Main Chart */}
      <Box
        sx={{
          p: 1,
          borderRadius: 2,
          bgcolor: "#ffffff",
          border: "1px solid #e0e0e0",
          boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
          mb: 1,
        }}
      >
        <CurveChart
          data={chartData}
          xLabel="Final Rating"
          yLabel="Employees"
          seriesName="Employee Count"
          fillArea
        />
      </Box>

      {/* Rating Distribution */}
              {barData.length > 0 && (
                <Box
                   sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: "#ffffff",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "text.secondary",
                      mb: 1,
                    }}
                  >
                    Rating Distribution
                  </Typography>

                  <BarChart data={barData} />
                </Box>
              )}

            </CardContent>
          </Card>
        </Grid>
        </Grid>


      </Box>

      {/*  Employee History Dialog  */}
      {selectedEmp && (
        <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle
            sx={{
              bgcolor: "hsl(144, 38%, 27%)",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            Employee History — {selectedEmp.empName}
            <IconButton size="small" onClick={() => setHistoryOpen(false)} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ mt: 2, p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Current Details
            </Typography>
            {currentYearRecord ? (
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={4}><MiniDetail label="Emp ID" value={currentYearRecord.empId} /></Grid>
                <Grid item xs={6} sm={4}><MiniDetail label="DOJ" value={dayjs(currentYearRecord.dateOfJoining).format("DD-MM-YYYY")} /></Grid>
                <Grid item xs={6} sm={4}><MiniDetail label="Group DOJ" value={currentYearRecord.groupDoj ? dayjs(currentYearRecord.groupDoj).format("DD-MM-YYYY"): "-" }/></Grid>                            
                 {currentYearRecord.groupDoj && currentYearRecord.employeeType && (
                <Grid item xs={6} sm={4}><MiniDetail label="Conversion" value={currentYearRecord.employeeType} /></Grid>)}
                {currentYearRecord.groupDoj && currentYearRecord.employeeType && (
                <Grid item xs={6} sm={4}> <MiniDetail label="On Roll Conversion On" value={dayjs(currentYearRecord.dateOfJoining).format("DD-MM-YYYY")}/></Grid>)}
                <Grid item xs={6} sm={4}><MiniDetail label="Payroll" value={currentYearRecord.payrollStatus} /></Grid>
                <Grid item xs={6} sm={4}><MiniDetail label="Grade" value={currentYearRecord.grade} /></Grid>
                <Grid item xs={6} sm={4}><MiniDetail label="Department" value={currentYearRecord.department} /></Grid>
                <Grid item xs={6} sm={4}><MiniDetail label="Current Designation" value={currentYearRecord.designation} /></Grid>
                {/* <Grid item xs={6} sm={4}><MiniDetail label="Updated Designation" value={currentYearRecord.prevDesignation || "-"} /></Grid> */}
                <Grid item xs={6} sm={4}>
                  <MiniDetail label="Current CTC" value={currentYearRecord.decodedCTC != null ? `₹${currentYearRecord.decodedCTC.toLocaleString("en-IN")}` : "-"} />
                </Grid>
                {/* <Grid item xs={6} sm={4}>
                  <MiniDetail label="Previous CTC" value={currentYearRecord.prevCTC != null ? `${currentYearRecord.prevCTC.toLocaleString("en-IN")}` : "-"} />
                </Grid> */}
                <Grid item xs={6} sm={4}>
                  {(() => {
                    const incrementAmt = decodeBase64(currentYearRecord.incrementAmount);
                    const currentCTC = currentYearRecord.decodedCTC;
                    const computed = currentCTC != null && incrementAmt != null ? currentCTC + incrementAmt : null;
                    const revisedCTC = computed ?? currentYearRecord.revisedCTC;
                    return (
                      <MiniDetail
                        label="Revised CTC"
                        value={revisedCTC != null ? `₹${revisedCTC.toLocaleString("en-IN")}` : "-"}
                        subText={incrementAmt != null ? `+ ₹${incrementAmt.toLocaleString("en-IN")}` : null}
                      />
                    );
                  })()}
                </Grid>
               </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No data available</Typography>
            )}
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Year Wise History
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Year</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Designation</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>CTC</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}> Revised CTC</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Promotion</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Final Rating</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyRows.length > 0 ? historyRows.map((rec, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{rec.pmsYear || "-"}</TableCell>
                    <TableCell>{rec.designation || "-"}</TableCell>
                    <TableCell>{rec.decodedCTC != null ? `₹${rec.decodedCTC.toLocaleString("en-IN")}` : "-"}</TableCell>
                    <TableCell>
                      {(() => {
                        const incrementAmt = decodeBase64(rec.incrementAmount);
                        const currentCTC = rec.decodedCTC;
                        const computed = currentCTC != null && incrementAmt != null ? currentCTC + incrementAmt : null;
                        const revisedCTC = computed ?? rec.revisedCTC;

                        return revisedCTC != null ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography variant="body2">
                            ₹{revisedCTC.toLocaleString("en-IN")}
                          </Typography>
                          {incrementAmt != null && (
                            <Typography
                              variant="caption"
                              sx={{ color: "#2e7d32", fontWeight: 600 }}
                            >
                              (+ ₹{incrementAmt.toLocaleString("en-IN")})
                            </Typography>
                          )}
                        </Box>
                        ) : (
                          "-"
                        );
                      })()}
                    </TableCell>                 {/* <TableCell>
                      {rec.ctcChange != null ? (
                        <Typography
                          variant="body2"
                          fontSize="0.82rem"
                          fontWeight={600}
                         >
                          {rec.ctcChange >= 0 ? `+${rec.ctcChange.toLocaleString("en-IN")}` : `${rec.ctcChange.toLocaleString("en-IN")}`}
                        </Typography>
                      ) : "-"}
                    </TableCell> */}
                    <TableCell>{rec.promotionType || "-"}</TableCell>
                    <TableCell>
                      {rec.hrHeadRating || rec.bhFinalRating ? (
                        <Chip label={rec.hrHeadRating || rec.bhFinalRating} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: "0.75rem" }} />
                      ) : "-"}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No history available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};

//Helpers
const DetailRow = ({
  label,
  value,
  chipBg,
  chipText,
  chipBorder,
  remark,
  recommendation,
  children,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      px: 1.2,
      py: 1,
      border: "1px solid #e2e8f0",
      borderRadius: 1.5,
      background: "linear-gradient(to right, #ffffff, #f8fafc)",
      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
      minHeight: 34,
    }}
  >
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 600,
        color: "#37474f",
      }}
    >
      {label}
    </Typography>

    <Box display="flex" alignItems="center" gap={0.5}>
      {children || (
        <Chip
          label={value || "-"}
          size="small"
          sx={{
            height: 24,
            fontSize: 12,
            fontWeight: 600,
            bgcolor: chipBg || "#f7f7f7",
            color: chipText || "#37474f",
            border: chipBorder || "1px solid #dcdcdc",
          }}
        />
      )}

    
      {recommendation && (
        <Chip
          label={recommendation}
          size="small"
          sx={{
            height: 22,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: "#e3f2fd",
            color: "#1565c0",
            border: "1px solid #90caf9",
          }}
        />
      )}
      {remark && (
        <Tooltip title={remark} arrow>
          <StickyNote2RoundedIcon
      sx={{
        fontSize: 16,
        color: "#757575",
        cursor: "pointer",
      }}
    />
        </Tooltip>
      )}
    </Box>
  </Box>
);
const MiniDetail = ({ label, value, subText }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography variant="body2" fontWeight={600} fontSize="0.82rem">{value || "-"}</Typography>
      {subText && <Typography variant="caption" sx={{ color: "#2e7d32", fontWeight: 600 }}>({subText})</Typography>}
    </Box>
  </Box>
);

//  Submitted Read-Only View 
const SubmittedView = ({ detail, isNAPS, status }) => (
  <Box sx={{ p: 1.5, bgcolor: "#f1f5f9", borderRadius: 2, border: "1px solid #cbd5e1" }}>
    <Grid container spacing={1}>
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <DetailRow label="Final Rating" value={detail?.hrHeadRating || detail?.bhFinalRating || "-"} />         
          {detail.department && <DetailRow label="Designation" value={detail.department} />}
          {isNAPS ? (
            <DetailRow label="Hike Per Month" value={detail.hikePerMonth || "-"} />
          ) : (
            <>
              <DetailRow label="Rating %" value={`${detail.incrementAsPerRating || "-"}%`} />
              <DetailRow label="Final Increment %"   value={detail.finalIncrement != null ? `${detail.finalIncrement}%` : "-"}
 />
            </>
          )}
         <DetailRow label="Additional %" value={`${detail.additionalIncrementPercentage || "-"}%`} />
          <DetailRow label="Current CTC" value={formatINR(detail.currentCTC)} />
          
          <DetailRow label="HR Status">
            <Chip
              label={detail?.hrStatus ?? detail?.bhStatus ??  "Sumbitted"}  
               variant="outlined"
              size="small"
              sx={{
                alignSelf: "flex-start",
                color: (detail?.hrStatus || detail?.bhStatus) === "Submitted" ? "#2e7d32" : "#f57f17",
                borderColor: (detail?.hrStatus ||  detail?.bhStatus) === "Submitted" ? "#2e7d32" : "#f57f17",
                fontWeight: 600,
                height: 24,
                fontSize: "0.75rem",
              }}
            />
          </DetailRow>
        </Box>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <DetailRow label="Promotion Type" value={detail.promotionType || "-"}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Chip
                label={detail.promotionType || "-"}
                size="small"
                sx={{ height: 24, fontSize: 12, fontWeight: 600, bgcolor: "#f7f7f7", color: "#37474f", border: "1px solid #dcdcdc" }}
              />
              {detail.promotionType?.includes("Promotion") && detail.promotionIncrementPercentage && detail.promotionIncrementPercentage !== "-" && (
                <Chip
                  label={`${detail.promotionIncrementPercentage}%`}
                  size="small"
                  sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: "#e8f5e9", color: "#2e7d32", border: "1px solid #81c784" }}
                />
              )}
            </Box>
          </DetailRow>
          {!isNAPS && detail.promotionGrade && <DetailRow label="Promotion Grade" value={detail.promotionGrade} />}
          {!isNAPS && <DetailRow label="Prorated Increase %"     value={`${detail.proRatedIncreasePercentage || "-"} %`}/>}
          <DetailRow label="Increment Amount" value={formatINR(detail.incrementAmount)} />
          <DetailRow label="Additional Amount" value={formatINR(detail.additionalIncrementAmount)} />

          {(() => {
            const incrementAmt = parseFloat(detail.incrementAmount);
            const currentCTC = parseFloat(detail.currentCTC);
            const computed = !isNaN(currentCTC) && !isNaN(incrementAmt) ? currentCTC + incrementAmt : null;
            const revisedCTC = computed ?? parseFloat(detail.revisedCTC);
            return (
              <DetailRow
                label="Revised CTC"
                value={revisedCTC != null && !isNaN(revisedCTC) ? `₹${revisedCTC.toLocaleString("en-IN")}` : "-"}
              />
            );
          })()}
         {detail.hrRemark && (
            <Tooltip title={detail.hrRemark.length > 40 ? detail.hrRemark : ""} arrow>
              <Box>
                <DetailRow label="Remarks" value={detail.hrRemark.length > 40 ? `${detail.hrRemark.slice(0, 40)}...` : detail.hrRemark} />
              </Box>
            </Tooltip>
          )}
 
        </Box>
      </Grid>
    </Grid>
  </Box>
);

const formatINR = (val) => {
  const num = parseFloat(val);
  if (isNaN(num)) return val || "-";
  return `₹${Math.round(num).toLocaleString("en-IN")}`;
};

// Inline Action Form (HEPL / NAPS)
const InlineActionForm = ({ record, designationOptions, onSubmit, onCancel, loading, getInitialFormValues, getIncrementMonths, getProRated, computeHEPLFinalValues, gradeOptions, onRatingChange, onValuesChange }) => {
  const isNAPS = record?.payrollStatus === "NAPS" || record?.payrollStatus === "NATS" || record?.payrollStatus === "IC";
  const initialValues = getInitialFormValues(record);
  const [selectedPromotionType, setSelectedPromotionType] = useState(initialValues.promotionType || "");
  const formBoxRef = useRef(null);
  const [shiftHeld, setShiftHeld] = useState(false);
  const [revisedCTCEditable, setRevisedCTCEditable] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [ratingResetDialogOpen, setRatingResetDialogOpen] = useState(false);
  const pendingRatingRef = useRef(null);
  const dialogJustClosed = useRef(false);
  const changeLogRef = useRef([]);

  const logChange = (trigger, values) => {
    changeLogRef.current.push({
      trigger,
      values: {
        bhFinalRating: values.bhFinalRating,
        additionalAmount: values.additionalAmount,
        additionalPercentage: values.additionalPercentage,
        incrementAmount: values.incrementAmount,
        revisedCTC: values.revisedCTC,
        hikePerMonth: values.hikePerMonth,
        promotionIncrementPercentage: values.promotionIncrementPercentage,
        finalIncrement: values.finalIncrement,
        currentCTC: values.currentCTC,
      },
      timestamp: new Date().toISOString(),
    });
  };

  useEffect(() => {
    const down = (e) => { if (e.key === "Shift") setShiftHeld(true); };
    const up = (e) => {
      if (e.key === "Shift") setShiftHeld(false);
      if (shiftHeld && !e.altKey && !e.ctrlKey && e.key.length === 1) {
        const letter = e.key.toLowerCase();
        const el = formBoxRef.current?.querySelector(`[data-hotkey="${letter}"]`);
        if (el) {
          e.preventDefault();
          const select = el.querySelector('[role="combobox"]');
          if (select) { select.focus(); select.dispatchEvent(new MouseEvent("mousedown", { bubbles: true })); return; }
          const input = el.querySelector("input:not([disabled]), textarea:not([disabled])");
          if (input) input.focus();
        }
      }
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, [shiftHeld]);

  const isPIP = selectedPromotionType.includes("PIP") || selectedPromotionType.includes("PC");
  const hasPromotion = selectedPromotionType?.includes("Promotion");
  const hasMarketStandard = selectedPromotionType?.includes("MarketStandard or Correction");


  // HEPL: rating change handler
  const handleFinalRatingChangeHEPL = (value, setFieldValue, values) => {
    const rating = RATING_OPTIONS_DATA.find((r) => r.ratingName === value);
    if (!rating) return;
    const pct = rating.percentage;
    setFieldValue("incrementAsPerRating", pct);
    const months = getIncrementMonths(record);
    const proRated = getProRated(pct, months);
    setFieldValue("proRatedIncreasePercentage", proRated);
    const updated = { ...values, bhFinalRating: value, incrementAsPerRating: pct, proRatedIncreasePercentage: proRated };
    const computed = computeHEPLFinalValues(updated);
    setFieldValue("finalIncrement", computed.finalIncrement);
    setFieldValue("incrementAmount", computed.incrementAmount);
    setFieldValue("revisedCTC", computed.revisedCTC);
    logChange("bhFinalRating", { ...updated, finalIncrement: computed.finalIncrement, incrementAmount: computed.incrementAmount, revisedCTC: computed.revisedCTC });
  };

  // HEPL: promotion type change
  const handlePromotionTypeChangeHEPL = (value, setFieldValue, values) => {
    setSelectedPromotionType(value);
    // Only reset fields if promotion type actually changed from initial
    if (value !== record?.promotionType) {
      setFieldValue("department", "");
      setFieldValue("promotionGrade", "");
      setFieldValue("promotionIncrementPercentage", 0);
      setFieldValue("promotionCorrections", 0);
    }
    const updated = { ...values, promotionType: value, ...(value !== record?.promotionType && { promotionIncrementPercentage: 0, promotionCorrections: 0 }) };
    const computed = computeHEPLFinalValues(updated);
    setFieldValue("finalIncrement", computed.finalIncrement);
    setFieldValue("incrementAmount", computed.incrementAmount);
    setFieldValue("revisedCTC", computed.revisedCTC);
  };

  // NAPS: rating change
  const handleFinalRatingChangeNAPS = (value, setFieldValue, values) => {
    const rating = RATING_OPTIONS_DATA.find((r) => r.ratingName === value);
    if (!rating) return;

    // If PC selected, reset everything
    if (value === "PC") {
      setFieldValue("promotionType", "PIP");
      setSelectedPromotionType("PIP");
      setFieldValue("hikePerMonth", 0);
      setFieldValue("additionalAmount", "0");
      setFieldValue("additionalPercentage", "0");
      setFieldValue("department", "");
      setFieldValue("incrementAmount", "0");
      const ctc = parseFloat(values.currentCTC) || 0;
      setFieldValue("revisedCTC", String(round2(ctc)));
      return;
    }

    // For NAPS: rating change only updates the rating display, not increment values
    // Increment is driven by hikePerMonth field only
    logChange("bhFinalRating", { ...values, bhFinalRating: value });
  };

  // NAPS: hike per month change
  const handleHikeChange = (e, setFieldValue, values) => {
    const hike = parseFloat(e.target.value) || 0;
    setFieldValue("hikePerMonth", hike);
    const baseInc = round2(hike * 12);
    const ctc = parseFloat(values.currentCTC) || 0;
    const additionalAmt = parseFloat(values.additionalAmount) || 0;
    const totalInc = round2(baseInc + additionalAmt);
    setFieldValue("incrementAmount", String(totalInc));
    setFieldValue("revisedCTC", String(round2(ctc + totalInc)));
    logChange("hikePerMonth", { ...values, hikePerMonth: hike, incrementAmount: String(totalInc), revisedCTC: String(round2(ctc + totalInc)) });
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values) => onSubmit({ ...values, log: changeLogRef.current })}
      >
        {({ values, setFieldValue, handleSubmit: formikSubmit,resetForm }) => {
          // Sync budget on every render
          useEffect(() => {
            onValuesChange?.({ revisedCTC: values.revisedCTC, currentCTC: values.currentCTC, incrementAmount: values.incrementAmount });
          }, [values.revisedCTC, values.currentCTC, values.incrementAmount]);

          const isFormValid = (() => {
            // if (!values.bhFinalRating || !values.promotionType || !values.hrRemark) return false;
            const pt = values.promotionType;
            const pip = pt === "PIP" || pt === "PC";
            if (!isNAPS && !pip) {
              const promo = pt?.includes("Promotion");
              if (promo && (!values.department || !values.promotionGrade)) return false;
              if (promo && (!values.promotionIncrementPercentage || Number(values.promotionIncrementPercentage) <= 0)) return false;
            }
            if (isNAPS && !pip) {
              const promo = pt?.includes("Promotion");
              if (promo && !values.department) return false;
              if (!values.hikePerMonth || Number(values.hikePerMonth) <= 0) return false;
            }
            return true;
          })();

          return (
          <Form>
            <Box ref={formBoxRef} sx={{
              display: "flex", flexDirection: "column", gap: 0.8,
              "& .MuiInputBase-input": { padding: "6px 10px !important", fontSize: "0.85rem" },
              "& .MuiOutlinedInput-root": { minHeight: 38 },
              "& .MuiInputBase-root": { height: 38 },
              "& .MuiSelect-select": { padding: "6px 10px !important", fontSize: "0.85rem" },
              ...(shiftHeld && {
                "& [data-hotkey]::after": {
                  content: "attr(data-hotkey)",
                  position: "absolute",
                  top: 2,
                  right: 4,
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#fff",
                  bgcolor: "#0f872b",
                  borderRadius: "3px",
                  px: "4px",
                  py: "1px",
                  textTransform: "uppercase",
                  zIndex: 2,
                  lineHeight: 1.2,
                },
                "& [data-hotkey]": { position: "relative" },
              }),
            }}>

              {/*Ratings Section */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  px: 1.5,
                  py: 1.2,
                  mb: 1.5,
                  borderRadius: 2,
                  background: "linear-gradient(145deg, #ffffff, #f0f4f8)",
                  border: "1px solid #d9e2ec",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 800, color: "#102a43", mb: 0.5, fontSize: 9, letterSpacing: 0.5 }}>
                  RATINGS & PROMOTION DETAILS
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6} sm={4} data-hotkey="f">
                      <FormikSelectInput
                        search
                        name="bhFinalRating"
                        label="Final Rating"
                        options={RATING_SELECT_OPTIONS}
                        placeholder="Select Rating"
                        size="small"
                        required
                        onChange={(e) => {
                          const newRating = e.target.value;
                          const hasAdditionalChanges = (parseFloat(values.additionalAmount) || 0) !== 0 || (parseFloat(values.additionalPercentage) || 0) !== 0;
                          if (hasAdditionalChanges) {
                            pendingRatingRef.current = { newRating, prevRating: values.bhFinalRating, setFieldValue, values };
                            setFieldValue("bhFinalRating", newRating);
                            setRatingResetDialogOpen(true);
                          } else {
                            setFieldValue("bhFinalRating", newRating);
                            if (newRating === "PC") {
                              setFieldValue("promotionType", "PIP");
                              setSelectedPromotionType("PIP");
                            } else if (values.promotionType === "PIP") {
                              setFieldValue("promotionType", "No Movement");
                              setSelectedPromotionType("No Movement");
                            }
                            onRatingChange?.(newRating);
                            isNAPS
                              ? handleFinalRatingChangeNAPS(newRating, setFieldValue, values)
                              : handleFinalRatingChangeHEPL(newRating, setFieldValue, values);
                          }
                        }}
                      />
                  </Grid>
                  {!isNAPS && (
                    <Grid item xs={6} sm={4}>
                      <FormikTextInput size="small" inputRestriction="allowAll" name="incrementAsPerRating" label="Rating %" disabled sx={{
                        "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000", color: "#000" }
                      }} />
                    </Grid>
                  )}
                  {isNAPS && !isPIP && (
                    <Grid item data-hotkey="h" xs={6} sm={4}>
                      <Box>
                        <FormikTextInput
                          inputRestriction="numeric"
                          name="hikePerMonth"
                          label="Hike Per Month"
                          size="small"
                          required
                          onChange={(e) => handleHikeChange(e, setFieldValue, values)}
                        />
                      </Box>
                    </Grid>
                  )}
                  <Grid item data-hotkey="p" xs={6} sm={4}>
                    <Box>
                      <FormikSelectInput
                        search
                        name="promotionType"
                        label="Promotion Type"
                        options={values.bhFinalRating === "PC" ? PROMOTION_TYPE_OPTIONS.filter(o => o.value === "PIP") : PROMOTION_TYPE_OPTIONS.filter(o => o.value !== "PIP")}
                        placeholder="Select Type"
                        size="small"
                        required
                        onChange={(e) => {
                          setFieldValue("promotionType", e.target.value);
                          if (isNAPS) {
                            setSelectedPromotionType(e.target.value);
                            setFieldValue("department", "");
                            if (e.target.value === "PIP") {
                              setFieldValue("bhFinalRating", "PC");
                              onRatingChange?.("PC");
                              handleFinalRatingChangeNAPS("PC", setFieldValue, values);
                            }
                          } else {
                            if (e.target.value === "PIP") {
                              setSelectedPromotionType(e.target.value);
                              setFieldValue("department", "");
                              setFieldValue("promotionGrade", "");
                              setFieldValue("promotionIncrementPercentage", 0);
                              setFieldValue("promotionCorrections", 0);
                              setFieldValue("bhFinalRating", "PC");
                              onRatingChange?.("PC");
                              handleFinalRatingChangeHEPL("PC", setFieldValue, { ...values, promotionType: e.target.value, promotionIncrementPercentage: 0, promotionCorrections: 0 });
                            } else {
                              handlePromotionTypeChangeHEPL(e.target.value, setFieldValue, values);
                            }
                          }
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Promotion fields (conditionally shown in same section) */}
                  {!isNAPS && !isPIP && hasPromotion && (
                    <>
                      <Grid item data-hotkey="d" xs={6} sm={4}>
                        <FormikSelectInput size="small" name="department" label="Designation" options={designationOptions} search placeholder="Select" required />
                      </Grid>
                      <Grid item data-hotkey="g" xs={6} sm={4}>
                        <FormikSelectInput size="small" name="promotionGrade" label="Promotion Grade" options={gradeOptions} search placeholder="Select" required />
                      </Grid>
                      <Grid item data-hotkey="t" xs={6} sm={4}>
                        <FormikTextInput inputRestriction="numeric" fullWidth name="promotionIncrementPercentage" label="Promotion %" size="small" max={100} min={0} maxLength={5} required
                          onKeyDown={(e) => {
                            if (e.key === '.' && e.target.value.includes('.')) e.preventDefault();
                          }}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\.{2,}/g, '.');
                            setFieldValue("promotionIncrementPercentage", value);
                            const updated = { ...values, promotionIncrementPercentage: value };
                            const computed = computeHEPLFinalValues(updated);
                            setFieldValue("finalIncrement", computed.finalIncrement);
                            setFieldValue("incrementAmount", computed.incrementAmount);
                            setFieldValue("revisedCTC", computed.revisedCTC);
                            logChange("promotionIncrementPercentage", { ...updated, finalIncrement: computed.finalIncrement, incrementAmount: computed.incrementAmount, revisedCTC: computed.revisedCTC });
                          }} />
                      </Grid>
                    </>
                  )}
                  {!isNAPS && !isPIP && hasMarketStandard && (
                    <Grid item data-hotkey="c" xs={6} sm={4}>
                      <FormikTextInput inputRestriction="allowAll" fullWidth name="promotionCorrections" label="Correction %" size="small" max={100} min={0} maxLength={3}
                        onChange={(e) => {
                          setFieldValue("promotionCorrections", e.target.value);
                          const updated = { ...values, promotionCorrections: e.target.value };
                          const computed = computeHEPLFinalValues(updated);
                          setFieldValue("finalIncrement", computed.finalIncrement);
                          setFieldValue("incrementAmount", computed.incrementAmount);
                          setFieldValue("revisedCTC", computed.revisedCTC);
                        }} />
                    </Grid>
                  )}
                  {isNAPS && !isPIP && hasPromotion && (
                    <Grid item data-hotkey="d" xs={6} sm={4}>
                      <FormikSelectInput size="small" name="department" label="Designation" options={designationOptions} search placeholder="Select" />
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Final Section  */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  px: 1.5,
                  py: 1.2,
                  borderRadius: 2,
                  background: "linear-gradient(145deg, #ffffff, #f0fdf4)",
                  border: "1px solid #bbf7d0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 800, color: "#166534", mb: 0.5, fontSize: 9, letterSpacing: 0.5 }}>
                  PAYABLE AMOUNT          
                </Typography>
                <Grid container spacing={1}>
                  {/* {!isNAPS && (
                    <Grid item xs={6} sm={4} md={3}>
                      <FormikTextInput size="small" inputRestriction="allowAll" name="finalIncrement" label="Final Increment %" disabled sx={{
                        "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000", color: "#000" }
                      }} />
                    </Grid>
                  )} */}
                  <Grid item data-hotkey="a" xs={6} sm={4} md={4}>
                    <FormikTextInput size="small" inputRestriction="numeric" name="additionalAmount" label="Additional Amount"
                      disabled={values.bhFinalRating === "PC" || values.promotionType === "PIP"}
                      value={parseFloat(values.additionalAmount) ? formatINR(values.additionalAmount) : values.additionalAmount}
                      onChange={(e) => {
                        const val = e.target.value;
                        const raw = val.replace(/[^0-9.]/g, "");
                        let amt = parseFloat(raw) || 0;
                        const ctc = parseFloat(values.currentCTC) || 0;
                        const maxAmt = ctc > 0 ? round2(ctc) : 0;
                        if (amt > maxAmt) amt = maxAmt;
                        setFieldValue("additionalAmount", String(amt));
                        const pct = ctc > 0 ? round2((amt / ctc) * 100) : 0;
                        setFieldValue("additionalPercentage", String(pct));
                        const updated = { ...values, additionalPercentage: String(pct), additionalAmount: String(amt) };

                        if (isNAPS) {
                          const baseInc = round2((parseFloat(values.hikePerMonth) || 0) * 12);
                          const totalInc = round2(baseInc + amt);
                          setFieldValue("incrementAmount", String(totalInc));
                          setFieldValue("revisedCTC", String(round2(ctc + totalInc)));
                          logChange("additionalAmount", { ...updated, incrementAmount: String(totalInc), revisedCTC: String(round2(ctc + totalInc)) });
                        } else {
                          const computed = computeHEPLFinalValues(updated);
                          setFieldValue("finalIncrement", computed.finalIncrement);
                          setFieldValue("incrementAmount", computed.incrementAmount);
                          setFieldValue("revisedCTC", computed.revisedCTC);
                          logChange("additionalAmount", { ...updated, finalIncrement: computed.finalIncrement, incrementAmount: computed.incrementAmount, revisedCTC: computed.revisedCTC });
                        }
                      }}
                    />
                  </Grid>
                  <Grid item data-hotkey="q" xs={6} sm={4} md={4}>
                    <FormikTextInput size="small" inputRestriction="numeric" name="additionalPercentage" label="Additional %"
                      disabled={values.bhFinalRating === "PC" || values.promotionType === "PIP"}
                      max={100}
                      min={0}
                      maxLength={5}
                      onKeyDown={(e) => {
                        if (e.key === '.' && e.target.value.includes('.')) e.preventDefault();
                      }}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\.{2,}/g, '.');
                        const pct = parseFloat(val) || 0;
                        setFieldValue("additionalPercentage", val);
                        const ctc = parseFloat(values.currentCTC) || 0;
                        const amt = round2((ctc * pct) / 100);
                        setFieldValue("additionalAmount", String(amt));
                        const updated = { ...values, additionalPercentage: val, additionalAmount: String(amt) };
                        if (isNAPS) {
                          const baseInc = round2((parseFloat(values.hikePerMonth) || 0) * 12);
                          const totalInc = round2(baseInc + amt);
                          setFieldValue("incrementAmount", String(totalInc));
                          setFieldValue("revisedCTC", String(round2(ctc + totalInc)));
                          logChange("additionalPercentage", { ...updated, incrementAmount: String(totalInc), revisedCTC: String(round2(ctc + totalInc)) });
                        } else {
                          const computed = computeHEPLFinalValues(updated);
                          setFieldValue("finalIncrement", computed.finalIncrement);
                          setFieldValue("incrementAmount", computed.incrementAmount);
                          setFieldValue("revisedCTC", computed.revisedCTC);
                          logChange("additionalPercentage", { ...updated, finalIncrement: computed.finalIncrement, incrementAmount: computed.incrementAmount, revisedCTC: computed.revisedCTC });
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={4}>
                    <FormikTextInput size="small" inputRestriction="allowAll" name="_incrementAmountDisplay" label="Increment Amt" disabled
                      value={formatINR(values.incrementAmount)}
                      sx={{ "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000", color: "#000" } }} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={4}>
                    <FormikTextInput size="small" inputRestriction="allowAll" name="_currentCTCDisplay" label="Current CTC" disabled
                      value={formatINR(values.currentCTC)}
                      sx={{ "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000", color: "#000" } }} />
                  </Grid>
                  <Grid item xs={6} sm={4} md={4}>
                    <Box onClick={() => { if (!revisedCTCEditable && !dialogJustClosed.current && values.bhFinalRating !== "PC" && values.promotionType !== "PIP") setConfirmDialogOpen(true); }}>
                      <FormikTextInput size="small" inputRestriction="allowAll" name="_revisedCTCDisplay" label="Revised CTC"
                        readOnly={!revisedCTCEditable || values.bhFinalRating === "PC" || values.promotionType === "PIP"}
                        value={formatINR(values.revisedCTC)}
                        onChange={revisedCTCEditable ? (e) => {
                          const raw = e.target.value.replace(/[^0-9.]/g, "");
                          const newRevised = parseFloat(raw) || 0;
                          setFieldValue("revisedCTC", raw);
                          const ctc = parseFloat(values.currentCTC) || 0;
                          if (isNAPS) {
                            const baseInc = round2((parseFloat(values.hikePerMonth) || 0) * 12);
                            const newAdditionalAmt = round2(Math.max(0, newRevised - ctc - baseInc));
                            const newAdditionalPct = ctc > 0 ? round2((newAdditionalAmt / ctc) * 100) : 0;
                            setFieldValue("additionalAmount", String(newAdditionalAmt));
                            setFieldValue("additionalPercentage", String(newAdditionalPct));
                            setFieldValue("incrementAmount", String(round2(baseInc + newAdditionalAmt)));
                            logChange("revisedCTC", { ...values, revisedCTC: raw, additionalAmount: String(newAdditionalAmt), additionalPercentage: String(newAdditionalPct), incrementAmount: String(round2(baseInc + newAdditionalAmt)) });
                          } else {
                            const basePct = getBaseIncrementPct(values);
                            const baseIncAmt = round2((ctc * basePct) / 100);
                            const newAdditionalAmt = round2(Math.max(0, newRevised - ctc - baseIncAmt));
                            const newAdditionalPct = ctc > 0 ? round2((newAdditionalAmt / ctc) * 100) : 0;
                            const totalPct = round2(basePct + newAdditionalPct);
                            setFieldValue("additionalAmount", String(newAdditionalAmt));
                            setFieldValue("additionalPercentage", String(newAdditionalPct));
                            setFieldValue("incrementAmount", String(round2(baseIncAmt + newAdditionalAmt)));
                            setFieldValue("finalIncrement", String(totalPct));
                            logChange("revisedCTC", { ...values, revisedCTC: raw, additionalAmount: String(newAdditionalAmt), additionalPercentage: String(newAdditionalPct), incrementAmount: String(round2(baseIncAmt + newAdditionalAmt)), finalIncrement: String(totalPct) });
                          }
                        } : undefined}
                        onBlur={revisedCTCEditable ? () => {
                          const ctc = parseFloat(values.currentCTC) || 0;
                          let newRevised = parseFloat(values.revisedCTC) || 0;
                          if (isNAPS) {
                            const baseInc = round2((parseFloat(values.hikePerMonth) || 0) * 12);
                            const minRevised = ctc + baseInc;
                            if (newRevised < minRevised) {
                              setFieldValue("revisedCTC", String(round2(minRevised)));
                              setFieldValue("additionalAmount", "0");
                              setFieldValue("additionalPercentage", "0");
                              setFieldValue("incrementAmount", String(baseInc));
                            }
                          } else {
                            const basePct = getBaseIncrementPct(values);
                            const baseIncAmt = round2((ctc * basePct) / 100);
                            const minRevised = ctc + baseIncAmt;
                            if (newRevised < minRevised) {
                              setFieldValue("revisedCTC", String(round2(minRevised)));
                              setFieldValue("additionalAmount", "0");
                              setFieldValue("additionalPercentage", "0");
                              setFieldValue("incrementAmount", String(baseIncAmt));
                              setFieldValue("finalIncrement", String(basePct));
                            }
                          }
                        } : undefined}
                        sx={{
                          "& .MuiInputBase-input": { cursor: !revisedCTCEditable ? "pointer" : "text" },
                          ...(revisedCTCEditable && { "& .MuiOutlinedInput-root": { bgcolor: "#fff8e1", border: "1px solid #ffb74d" } }),
                        }} />
                    </Box>
                    <CustomAlert
                      open={confirmDialogOpen}
                      onCancel={() => { dialogJustClosed.current = true; setConfirmDialogOpen(false); setTimeout(() => { dialogJustClosed.current = false; }, 100); }}
                      onSubmit={() => { setRevisedCTCEditable(true); setConfirmDialogOpen(false); }}
                      title="Edit Revised CTC?"
                      message="Are you sure you want to manually edit the Revised CTC? This will recalculate Additional Amount and Additional Percentage."
                      cancelText="Cancel"
                      submitText="Yes, Edit"
                      width="600px"
                    />
                    <CustomAlert
                      open={ratingResetDialogOpen}
                      onCancel={() => {
                        const { prevRating, setFieldValue: sfv } = pendingRatingRef.current;
                        sfv("bhFinalRating", prevRating);
                        setRatingResetDialogOpen(false);
                        pendingRatingRef.current = null;
                      }}
                      onSubmit={() => {
                        const { newRating, setFieldValue: sfv, values: vals } = pendingRatingRef.current;
                        sfv("additionalAmount", "0");
                        sfv("additionalPercentage", "0");
                        if (newRating === "PC") {
                          sfv("promotionType", "PIP");
                          setSelectedPromotionType("PIP");
                        } else if (vals.promotionType === "PIP") {
                          sfv("promotionType", "No Movement");
                          setSelectedPromotionType("No Movement");
                        }
                        onRatingChange?.(newRating);
                        const resetVals = { ...vals, additionalAmount: "0", additionalPercentage: "0" };
                        isNAPS
                          ? handleFinalRatingChangeNAPS(newRating, sfv, resetVals)
                          : handleFinalRatingChangeHEPL(newRating, sfv, resetVals);
                        setRatingResetDialogOpen(false);
                        pendingRatingRef.current = null;
                      }}
                      title="Rating Changed"
                      message="You have made changes to Additional Amount / Additional %. Changing the rating will reset these values. Do you want to proceed?"
                      cancelText="Cancel"
                      submitText="Yes, Reset"
                      width="600px"
                    />
                  </Grid>
                  {!isNAPS && (
                  <Grid item xs={6} sm={4} md={4}>
                    <FormikTextInput size="small" inputRestriction="allowAll" name="finalIncrement" label="Final Increment %" disabled
                      sx={{
                        "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#2e7d32", color: "#2e7d32", fontWeight: 700 },
                        "& .MuiOutlinedInput-root": { bgcolor: "#e8f5e9", border: "1px solid #a5d6a7" },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#81c784" },
                        "& .MuiInputLabel-root": { fontWeight: 600, color: "#388e3c" },
                      }} />
                  </Grid>
                  )}
                  <Grid item data-hotkey="r" xs={12} sm={12} md={12}>
                    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <FormikTextInput size="small" name="hrRemark" label="Remarks" placeholder="Enter Remarks" />
                      </Box>
                      <Button variant="outlined" color="error" size="small" onClick={()=>{ resetForm(); onCancel()}} sx={{ textTransform: "none", fontWeight: 600, mb: 1 }}>
                        Cancel
                      </Button>
                      <Button variant="contained" size="small" type="submit" disabled={loading || !isFormValid} sx={{ textTransform: "none", fontWeight: 600, mb: 1 }}>
                        {loading ? "Submitting..." : "Submit"}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

            </Box>
          </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default HrHeadPMS;
